import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { projectsData } from "../data/projectsData";
import "../styles/globals.css";

const CARD_ACCENTS = [
  "#01E12D", // brand green
  "#256af4", // brand blue
  "#f42536", // brand red
  "#141414", // ink/dark
  "#01E12D", // brand green
  "#256af4", // brand blue
];

const GLYPHS = ["❖", "◷", "⛨", "✺", "◈", "⬡", "◉", "⬟"];

interface ProjectsSectionProps {
  /** Voice differs per face: "My Projects" on the profile, "Our Projects" on the agency. */
  eyebrow?: string;
  heading?: string;
}

export function ProjectsSection({
  eyebrow = "What I am proud of",
  heading = "My Projects",
}: ProjectsSectionProps = {}) {
  const deckRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLElement>(null);
  const curRef = useRef<HTMLSpanElement>(null);
  const movedRef = useRef(false);
  const centerIdxRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const deck = deckRef.current;
    const barEl = barRef.current;
    const curEl = curRef.current;
    if (!deck || !barEl || !curEl) return;

    const cards = [...deck.querySelectorAll<HTMLElement>(".gc-card")];
    const reduce = matchMedia("(prefers-reduced-motion:reduce)").matches;
    let hoverIdx = -1;
    let ticking = false;

    function render() {
      ticking = false;
      if (!deck) return;
      const r = deck.getBoundingClientRect();
      const center = r.left + r.width / 2;
      let best = 0;
      let bd = Infinity;

      cards.forEach((card, i) => {
        const cr = card.getBoundingClientRect();
        const cc = cr.left + cr.width / 2;
        let off = (cc - center) / cr.width;
        off = Math.max(-3.2, Math.min(3.2, off));
        const a = Math.abs(off);
        if (a < bd) { bd = a; best = i; }
        const hov = i === hoverIdx;

        let tz: number, rotY: number, scale: number, z: number, op: number;
        if (reduce) {
          rotY = 0; tz = 0;
          scale = hov ? 1.05 : (1 - Math.min(a * 0.06, 0.22));
          z = Math.round(1000 - a * 100) + (hov ? 3000 : 0);
          op = 1 - Math.min(a * 0.16, 0.5);
        } else {
          rotY = off * -21;
          tz = (hov ? 90 : -a * 160) + (a < 0.5 ? 40 : 0);
          scale = hov ? 1.1 : (1 - Math.min(a * 0.1, 0.34));
          z = Math.round(1000 - a * 100) + (hov ? 3000 : 0);
          op = 1 - Math.min(a * 0.17, 0.55);
        }

        card.style.transform = `translateZ(${tz}px) rotateY(${rotY}deg) scale(${scale})`;
        card.style.zIndex = String(z);
        card.style.opacity = String(op);
        card.classList.toggle("is-center", (a < 0.5 && !hov) || hov);
      });

      centerIdxRef.current = best;

      const max = deck.scrollWidth - deck.clientWidth;
      const pct = max > 0 ? deck.scrollLeft / max : 0;
      if (barEl) barEl.style.width = (15 + pct * 85) + "%";
      if (curEl) curEl.textContent = String(best + 1).padStart(2, "0");
    }

    function queue() {
      if (!ticking) { ticking = true; requestAnimationFrame(render); }
    }

    deck.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);

    cards.forEach((c, i) => {
      c.addEventListener("pointerenter", () => { if (i === centerIdxRef.current) { hoverIdx = i; queue(); } });
      c.addEventListener("pointerleave", () => { if (hoverIdx === i) { hoverIdx = -1; queue(); } });
      c.addEventListener("focus", () => {
        c.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      });
    });

    // One card's worth of travel. Measured from the cards themselves rather than
    // assumed, because the card width and the overlap between them both change
    // with the breakpoint (see --gc-card-w / --gc-card-overlap in globals.css).
    function step(d: number) {
      if (!deck || cards.length === 0) return;
      const advance = cards.length > 1
        ? cards[1].offsetLeft - cards[0].offsetLeft
        : cards[0].offsetWidth;
      deck.scrollBy({ left: d * advance, behavior: "smooth" });
    }

    const prevBtn = document.getElementById("gc-prev");
    const nextBtn = document.getElementById("gc-next");
    if (prevBtn) prevBtn.onclick = () => step(-1);
    if (nextBtn) nextBtn.onclick = () => step(1);

    function handleKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    }
    window.addEventListener("keydown", handleKey);

    // Drag to scroll — use window listeners instead of setPointerCapture
    // so the click event fires normally on the card target
    let down = false, sx = 0, ss = 0;

    function onPointerDown(e: PointerEvent) {
      down = true; movedRef.current = false; sx = e.clientX; ss = deck!.scrollLeft;
      deck!.classList.add("gc-dragging");
    }
    function onWindowPointerMove(e: PointerEvent) {
      if (!down) return;
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 8) movedRef.current = true;
      deck!.scrollLeft = ss - dx;
    }
    function onWindowPointerUp() {
      if (!down) return;
      down = false;
      deck!.classList.remove("gc-dragging");
    }

    deck.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onWindowPointerMove);
    window.addEventListener("pointerup", onWindowPointerUp);
    window.addEventListener("pointercancel", onWindowPointerUp);

    render();
    const t = setTimeout(render, 40);

    return () => {
      deck.removeEventListener("scroll", queue);
      window.removeEventListener("resize", queue);
      window.removeEventListener("keydown", handleKey);
      deck.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerUp);
      clearTimeout(t);
    };
  }, []);

  const featuredProjects = projectsData.filter((p) => p.featured);
  const total = featuredProjects.length;

  return (
    <section className="relative w-full overflow-hidden bg-[#e7f1f1]">
      {/* Header */}
      <div className="relative z-10 pt-16 pb-4 px-6 max-w-[1180px] mx-auto flex flex-col items-center gap-5">
        <p className="font-['Space_Mono',monospace] not-italic text-[12px] md:text-[14px] leading-[20px] tracking-[0.7px] uppercase text-[#666] text-center">
          {eyebrow}
        </p>
        <h2 className="font-['Outfit',sans-serif] font-black text-[48px] md:text-[96px] leading-tight md:leading-[96px] tracking-[-1.2px] md:tracking-[-2.4px] text-[#141414] text-center">
          {heading}
        </h2>
        <p className="font-['Outfit',sans-serif] font-normal text-[18px] leading-[28px] text-[#666] text-center">
          Drag the deck or use the arrows — click the front card to view the case.
        </p>

        {/* Counter + Nav */}
        <div className="flex items-center gap-4 mt-2">
          <span className="font-['Space_Mono',monospace] text-[13px] text-[#666]">
            [&nbsp;<b ref={curRef} className="text-[#141414] font-bold">01</b>&nbsp;/&nbsp;
            <span className="text-[#141414]">{String(total).padStart(2, "0")}</span>&nbsp;]
          </span>
          <div className="flex gap-2">
            <button
              id="gc-prev"
              aria-label="Previous project"
              className="w-12 h-12 rounded-full border-[1.5px] border-[#141414] text-[#141414] text-[20px] font-bold grid place-items-center transition-all duration-150 hover:bg-[#141414] hover:text-[#e7f1f1] active:scale-90 focus-visible:outline-2 focus-visible:outline-[#01E12D] focus-visible:outline-offset-2"
            >
              ‹
            </button>
            <button
              id="gc-next"
              aria-label="Next project"
              className="w-12 h-12 rounded-full border-[1.5px] border-[#141414] text-[#141414] text-[20px] font-bold grid place-items-center transition-all duration-150 hover:bg-[#141414] hover:text-[#e7f1f1] active:scale-90 focus-visible:outline-2 focus-visible:outline-[#01E12D] focus-visible:outline-offset-2"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* 3D Coverflow Deck */}
      <div className="gc-deck-wrap relative -mx-0">
        <div ref={deckRef} className="gc-deck">
          {featuredProjects.map((project, i) => {
            const accent = CARD_ACCENTS[i % CARD_ACCENTS.length];
            const glyph = GLYPHS[i % GLYPHS.length];
            const shortDesc = project.description.length > 250
              ? project.description.slice(0, 247) + "…"
              : project.description;
            const tags = project.role.slice(0, 3);

            return (
              <article
                key={project.id}
                className="gc-card"
                tabIndex={0}
                style={{ "--gc-accent": accent } as React.CSSProperties}
                onClick={(e) => {
                  if (movedRef.current) return;
                  if (centerIdxRef.current === i) {
                    navigate(`/projects/${project.slug}`);
                  } else {
                    (e.currentTarget as HTMLElement).scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                  }
                }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/projects/${project.slug}`); }}
                role="button"
                aria-label={`View ${project.title} project`}
              >
                <div
                  className="gc-card-inner"
                  style={{ "--gc-i": i } as React.CSSProperties}
                >
                  {/* Left column: Hero Image */}
                  <div className="relative w-[280px] h-full rounded-xl overflow-hidden mr-6 flex-shrink-0 max-[560px]:w-full max-[560px]:h-[140px] max-[560px]:mr-0 max-[560px]:mb-3">
                    <img
                      src={project.heroImage}
                      alt={project.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div
                      className="absolute inset-0"
                      style={{ background: `color-mix(in srgb, ${accent} 15%, transparent)` }}
                    />
                    <span
                      className="absolute bottom-2 right-2 font-['Space_Mono',monospace] font-bold text-[28px] max-[560px]:text-[20px] leading-none"
                      style={{ color: accent, filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.5))" }}
                    >
                      {glyph}
                    </span>
                  </div>

                  {/* Right column: Text Content */}
                  <div className="flex flex-col py-2 max-[560px]:py-0 gap-3 justify-between min-w-0 max-[560px]:flex-1">
                    {/* Top row: ID + scope badge */}
                    <div className="flex items-center gap-2">
                      <span
                        className="font-['Space_Mono',monospace] text-[14px] max-[560px]:text-[11px] text-[#666] tracking-[0.08em] flex-shrink-0"
                      >
                        {`P-${String(i + 1).padStart(2, "0")}`}
                      </span>
                      <span
                        className="ml-auto text-[12px] max-[560px]:text-[9px] font-extrabold tracking-[0.16em] max-[560px]:tracking-[0.08em] uppercase rounded-full px-[12px] py-[6px] max-[560px]:px-[8px] max-[560px]:py-[3px] whitespace-nowrap"
                        style={{
                          color: accent,
                          background: `color-mix(in srgb, ${accent} 12%, transparent)`,
                        }}
                      >
                        {project.scope.split(",")[0].trim()}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-['Outfit',sans-serif] font-black text-[38px] max-[560px]:text-[22px] leading-tight tracking-[-0.02em] text-[#141414]">
                      {project.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[16px] max-[560px]:text-[12px] font-medium text-[#666] leading-[1.6] max-[560px]:line-clamp-3">
                      {shortDesc}
                    </p>

                    {/* Stack / roles */}
                    <div className="flex flex-wrap gap-2 max-[560px]:gap-1">
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-[12px] max-[560px]:text-[10px] font-semibold text-[#141414] px-3 max-[560px]:px-2 py-1.5 max-[560px]:py-1 rounded-full border-[1.5px] border-[rgba(0,0,0,0.12)] whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* View project link */}
                    <div className="flex items-center justify-center pt-3 border-t border-[rgba(0,0,0,0.08)]">
                      <span
                        className="flex items-center gap-2 font-['Space_Mono',monospace] text-[16px] max-[560px]:text-[13px] font-bold"
                        style={{ color: accent }}
                      >
                        view case <span className="gc-go-arrow">→</span>
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {/* Progress rail */}
      <div className="max-w-[1180px] mx-auto px-6 pb-10">
        <div className="h-[3px] bg-[rgba(0,0,0,0.1)] rounded-full overflow-hidden mt-3">
          <i
            ref={barRef as React.RefObject<HTMLElement>}
            className="block h-full w-[18%] rounded-full bg-[linear-gradient(90deg,#01E12D,#256af4,#f42536)]"
            style={{ transition: "width 0.12s linear" }}
          />
        </div>
        <p className="font-['Space_Mono',monospace] text-[11px] text-[#666] text-center mt-3 tracking-[0.05em]">
          drag&nbsp;·&nbsp;scroll&nbsp;·&nbsp;← →&nbsp;·&nbsp;tab
        </p>
      </div>
    </section>
  );
}


